<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { ElMessageBox } from 'element-plus'
import type { FormFieldDef, PageDefinition, SelectOption } from '@/types/page'
import { usePrototypeStore } from '@/stores/prototype'
import ProField from './ProField.vue'
import AppIcon from '@/components/AppIcon.vue'

const props = withDefaults(defineProps<{
  open: boolean
  title: string
  description?: string
  fields?: FormFieldDef[]
  confirmText?: string
  tone?: 'primary' | 'danger' | 'default'
  loading?: boolean
  definition?: PageDefinition
}>(), {
  description: '',
  fields: () => [],
  confirmText: '确认',
  tone: 'primary',
  loading: false,
  definition: undefined,
})

const emit = defineEmits<{
  close: []
  confirm: [values: Record<string, unknown>]
}>()

const store = usePrototypeStore()
const form = reactive<Record<string, unknown>>({})
const errors = reactive<Record<string, string>>({})
const body = ref<HTMLElement | null>(null)
const snapshot = ref('')

const computedFields = computed(() => props.fields)
const dirty = computed(() => JSON.stringify(form) !== snapshot.value)

function serialize(): string {
  return JSON.stringify(form)
}

function options(field: FormFieldDef): SelectOption[] {
  if (field.optionSource && props.definition) {
    return store.options(props.definition, field.optionSource)
  }
  return field.options ?? []
}

function initialize(): void {
  Object.keys(form).forEach(key => delete form[key])
  Object.keys(errors).forEach(key => delete errors[key])
  for (const field of props.fields) {
    form[field.key] = field.defaultValue ?? (field.type === 'multiselect' || field.type === 'tags' ? [] : field.type === 'switch' ? false : '')
  }
  snapshot.value = serialize()
  nextTick(() => {
    const input = body.value?.querySelector('input, textarea, button') as HTMLElement | null
    input?.focus()
  })
}

function validate(): boolean {
  Object.keys(errors).forEach(key => delete errors[key])
  let valid = true
  for (const field of props.fields) {
    const value = form[field.key]
    const empty = value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length)
    if (field.required && empty) {
      errors[field.key] = `请填写${field.label}`
      valid = false
    }
    if (!empty && field.pattern && !new RegExp(field.pattern).test(String(value))) {
      errors[field.key] = field.patternMessage ?? `${field.label}格式不正确`
      valid = false
    }
  }
  return valid
}

function confirm(): void {
  if (!validate()) return
  emit('confirm', JSON.parse(JSON.stringify(form)) as Record<string, unknown>)
}

watch(() => props.open, open => {
  if (open) initialize()
})

function settle(done?: () => void): void {
  if (done) done()
  emit('close')
}

async function requestClose(done?: () => void): Promise<void> {
  if (!dirty.value || props.loading) {
    settle(done)
    return
  }
  try {
    await ElMessageBox.confirm('当前操作填写了内容但尚未提交，关闭会放弃这些输入。', '放弃未提交的内容', {
      type: 'warning',
      confirmButtonText: '放弃并关闭',
      cancelButtonText: '继续填写',
      distinguishCancelAndClose: true,
    })
    settle(done)
  } catch {
    // Keep editing.
  }
}

function close(): void {
  void requestClose()
}

function beforeClose(done: () => void): void {
  void requestClose(done)
}

function beforeUnload(event: BeforeUnloadEvent): void {
  if (!props.open || !dirty.value) return
  event.preventDefault()
  event.returnValue = ''
}

onMounted(() => window.addEventListener('beforeunload', beforeUnload))
onBeforeUnmount(() => window.removeEventListener('beforeunload', beforeUnload))

onBeforeRouteLeave(async () => {
  if (!props.open || !dirty.value) return true
  try {
    await ElMessageBox.confirm('当前操作填写了内容但尚未提交，离开页面会放弃这些输入。', '放弃未提交的内容', {
      type: 'warning',
      confirmButtonText: '放弃并离开',
      cancelButtonText: '继续填写',
      distinguishCancelAndClose: true,
    })
    return true
  } catch {
    return false
  }
})
</script>

<template>
  <el-dialog
    :model-value="open"
    :width="'440px'"
    :show-close="false"
    :close-on-click-modal="false"
    :before-close="beforeClose"
    align-center
    class="action-dialog"
  >
    <template #header>
      <div class="action-dialog__head">
        <span class="action-dialog__icon" :class="`action-dialog__icon--${tone}`">
          <AppIcon :name="tone === 'danger' ? 'warning' : 'info'" :size="19" />
        </span>
        <div>
          <h2>{{ title }}</h2>
          <p v-if="description">{{ description }}</p>
        </div>
        <button type="button" class="icon-button" aria-label="关闭" @click="close"><AppIcon name="close" :size="17" /></button>
      </div>
    </template>
    <div ref="body" class="action-dialog__body">
      <div v-for="field in computedFields" :key="field.key" class="action-dialog__field">
        <ProField v-model="form[field.key]" :field="field" :options="options(field)" :error="errors[field.key]" />
      </div>
    </div>
    <template #footer>
      <div class="action-dialog__foot">
        <button type="button" class="btn" :disabled="loading" @click="close">取消</button>
        <button type="button" class="btn" :class="tone === 'danger' ? 'btn--danger' : 'btn--primary'" :disabled="loading" @click="confirm">
          <span v-if="loading" class="button-spinner" aria-hidden="true"></span>
          {{ loading ? '处理中…' : confirmText }}
        </button>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped>
.action-dialog__head { display: flex; align-items: flex-start; gap: 11px; }
.action-dialog__head h2 { margin: 0; font-size: 16px; color: var(--c-ink); line-height: 1.35; }
.action-dialog__head p { margin: 4px 0 0; font-size: 11px; color: var(--c-muted); line-height: 1.55; max-width: 340px; }
.action-dialog__head .icon-button { margin-left: auto; }
.action-dialog__icon { width: 38px; height: 38px; border-radius: 10px; display: grid; place-items: center; background: var(--c-info-soft); color: var(--c-interactive); flex: 0 0 auto; }
.action-dialog__icon--danger { background: var(--c-danger-soft); color: var(--c-danger); }
.action-dialog__body { display: flex; flex-direction: column; gap: 12px; padding: 4px 2px 0; max-height: min(48vh, 380px); overflow: auto; }
.action-dialog__field { min-width: 0; }
.action-dialog__foot { display: flex; justify-content: flex-end; gap: 8px; }
.button-spinner { width: 12px; height: 12px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: spin .8s linear infinite; }
</style>
